import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, Label } from 'reactstrap';
import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import SweetAlert from 'react-bootstrap-sweetalert';
import { getQuoteBuilderURL } from '../../../helpers/common';
import styled from 'styled-components';
import renderFields from './common/render-caf-fields';
import ironMan from '../../../images/iron-man.jpg';
import { useHistory } from 'react-router';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';

/**
 * graphQL query for all the data needed for customise proposal UI
 * Quote - quote based from the passed quote id in the URL
 **/
const DATA = gql`
  query Data($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      Description
      PrimaryContact
      CustomerId
      WorkflowStep
      Status
      QuoteSites {
        QuoteSiteId
        Name
      }
    }

    CAFTemplates {
      CAFTemplateId
      Name
      Form
    }

    CAFSections {
      CAFSectionId
      Name
      SortOrder
    }

    QuoteCAFTemplates(QuoteId: $QuoteId) {
      QuoteCAFTemplateId
      Data
      QuoteId
    }

    CustomerDetails(QuoteId: $QuoteId) {
      Company
      FirstName
      LastName
      Email
      Abn
      Phone
      Mobile
      AddressLine1
      AddressLine2
      Suburb
      State
      Postcode
    }
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      CompanyName
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
    }
  }
`;

const UPDATE_QUOTE_CAF_TEMPLATE = gql`
  mutation updateQuoteCAFTemplate(
    $QuoteCAFTemplateInput: QuoteCAFTemplateInput
  ) {
    updateQuoteCAFTemplate(input: $QuoteCAFTemplateInput) {
      QuoteCAFTemplateId
      Data
      QuoteId
    }
  }
`;

const Section = styled.div`
  margin-block: 20px;
`;

const SectionHeader = styled.div`
  color: #fff;
  background: #3f86ff;
  padding: 10px 15px;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const Template = styled.div`
  padding: 15px;
  border: 1px solid #ccc;
  margin-top: 5px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const FieldLabel = styled(Label)`
  flex: 0 0 20%;
  margin-bottom: 0;
`;

const FieldInput = styled.div`
  flex: 1;
`;

const buildPrefillMap = (customer) => ({
  company: customer?.Company || '',
  email: customer?.Email || '',
  firstname: customer?.FirstName || '',
  surname: customer?.LastName || '',
  abn: customer?.Abn || '',
  phone: customer?.Phone || '',
  mobile: (customer?.Mobile || '').replace(/^\+61/, '0'),
  address: customer?.AddressLine1 || '',
  address2: customer?.AddressLine2 || '',
  suburb: customer?.Suburb || '',
  state: customer?.State || '',
  postcode: customer?.Postcode || '',
});

const applyPrefill = (templates, customerData) => {
  if (!customerData) return templates;
  const prefillMap = buildPrefillMap(customerData);
  return Object.fromEntries(
    Object.entries(templates).map(([sectionId, sectionData]) => {
      if (sectionData.name !== 'Customer') return [sectionId, sectionData];
      return [sectionId, {
        ...sectionData,
        templates: sectionData.templates.map(template => ({
          ...template,
          fields: template.fields.map(field => {
            if (!(field.name in prefillMap)) return field;
            return { ...field, prefill_value: prefillMap[field.name] || field.prefill_value || '', readonly: !!prefillMap[field.name] };
          }),
        })),
      }];
    })
  );
};

const QuoteBuilderPrefillCAF = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const history = useHistory();

  // initialize states
  const [disableQuote, setDisableQuote] = useState(false);
  const [quoteCAFTemplates, setQuoteCAFTemplates] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const { unblockRef, leavePrompt } = useUnsavedChanges(isDirty);

  // initialize apollo graphql queries and mutations
  const { data, loading } = useQuery(DATA, {
    variables: { QuoteId: props.quoteId },
  });
  const [updateQuote] = useMutation(UPDATE_QUOTE);
  const [updateQuoteCAFTemplate] = useMutation(UPDATE_QUOTE_CAF_TEMPLATE);
  const [invalidInput, setInvalidInput] = useState({});
  const [confirmation, setConfirmation] = useState(false);
  const [toggle, setToggle] = useState({});
  useEffect(() => {
    if (data && data.Quote) {
      // disable screen when status is accepted, declined or voided
      const status = data.Quote.Status;
      setDisableQuote(status === 'accepted');

      if (data.QuoteCAFTemplates.length > 0) {
        const quoteCAFTemplate = data.QuoteCAFTemplates[0];
        setQuoteCAFTemplates(applyPrefill(JSON.parse(quoteCAFTemplate.Data), data.CustomerDetails));
      }

      if (!['PREFILL_CAF', 'CUSTOMER_ACCEPTED'].includes(data.Quote.WorkflowStep)) {
        history.replace(getQuoteBuilderURL(data.Quote.WorkflowStep, data.Quote.QuoteId));
        return;
      }
    }
  }, [data, history]);

  const goToPreviousStep = (e) => {
    e.preventDefault();
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: 'CUSTOMISE_CAF',
        },
      },
    }).then(() => {
      history.replace(`/customise-caf/${data.Quote.QuoteId}`);
    });
  };

  const proceedNextStep = (e) => {
    e.preventDefault();

    document.querySelectorAll('.is-invalid').forEach(el => el.classList.remove('is-invalid'));

    let firstError = null;
    const fieldCounts = {};

    Object.values(quoteCAFTemplates).forEach((sectionData) => {
      ((sectionData && sectionData.templates) || []).filter(t => !t.hidden).forEach((item) => {
        const { fields = [] } = item || {};
        fields.filter(f => !f.hidden).forEach((field) => {
          let {
            type = '',
            validation = {},
            name = '',
            title = '',
          } = field;

          fieldCounts[name] = (fieldCounts[name] || 0) + 1;
          const el = document.querySelectorAll(`[name="${name}"]`)[fieldCounts[name] - 1];

          const { min = 0 } = validation || {};

          let errorMessage = null;

          if (field.validation?.isRequired && field.validation?.isReadOnly && !field.prefill_value) {
            errorMessage = `${title} is required.`;
          } else if (
            (type.toLowerCase() === 'text' || type.toLowerCase() === 'textarea') &&
            field.prefill_value && field.prefill_value.length < min
          ) {
            errorMessage = `Minimum ${min} characters required in ${title}.`;
          } else if (type.toLowerCase() === 'email') {
            const pattern = new RegExp(
              /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
            );
            if (field.prefill_value && !pattern.test(field.prefill_value)) {
              errorMessage = `Invalid e-mail address in ${title}`;
            }
          }

          if (errorMessage && el) {
            el.classList.add('is-invalid');
            if (!firstError) firstError = { message: errorMessage };
          }
        });
      });
    });

    if (!firstError) {
      setConfirmation(true);
    } else {
      setInvalidInput({ error: true, ...firstError });
    }
  };

  const saveQuoteCAFTemplate = () => {
    setConfirmation(false);
    if (data) {
      const payload = {
        QuoteId: parseInt(data.Quote.QuoteId, 10),
        Data: JSON.stringify(quoteCAFTemplates),
      };
      const template = data.QuoteCAFTemplates[0];
      if (template) {
        updateQuoteCAFTemplate({
          variables: {
            QuoteCAFTemplateInput: {
              ...payload,
              QuoteCAFTemplateId: parseInt(template.QuoteCAFTemplateId, 10),
            },
          },
        });
      }
      updateQuote({
        variables: {
          QuoteInput: {
            QuoteId: data.Quote.QuoteId,
            WorkflowStep: 'SEND_FOR_SIGNING',
          },
        },
      }).then(() => {
        if (unblockRef.current) { unblockRef.current(); unblockRef.current = null; }
        history.replace(`/send-for-signing/${data.Quote.QuoteId}`);
      });
    }
  };

  const ADDRESS_FIELDS = ['address', 'suburb', 'state', 'postcode'];

  const onTogglePropagation = (category, sectionIndex) => {
    const quoteTemplates = Object.assign({}, quoteCAFTemplates);
    const tmpl = quoteTemplates[category].templates[sectionIndex];
    quoteTemplates[category].templates[sectionIndex] = {
      ...tmpl,
      disable_propagation: !tmpl.disable_propagation,
    };
    setQuoteCAFTemplates(quoteTemplates);
    setIsDirty(true);
  };

  const getPropagationPeers = (template) => {
    const peers = [];
    Object.entries(quoteCAFTemplates).forEach(([secId, secData]) => {
      secData.templates.forEach((tmpl, tmplIdx) => {
        if (tmpl !== template && tmpl.propagate_address && !tmpl.hidden && tmpl.site_id === template.site_id) {
          peers.push({ secId, tmplIdx, tmpl });
        }
      });
    });
    return peers;
  };

  const onFieldChange = (fieldMeta, value) => {
    const { category, sectionIndex, fieldIndex, fieldName } = fieldMeta;
    const quoteTemplates = Object.assign({}, quoteCAFTemplates);
    quoteTemplates[category].templates[sectionIndex].fields[fieldIndex].prefill_value = value;

    if (ADDRESS_FIELDS.includes(fieldName)) {
      const sourceTemplate = quoteTemplates[category].templates[sectionIndex];
      if (sourceTemplate.propagate_address && !sourceTemplate.disable_propagation) {
        getPropagationPeers(sourceTemplate).forEach(({ secId, tmplIdx, tmpl }) => {
          if (tmpl.disable_propagation) return;
          ADDRESS_FIELDS.forEach(addrField => {
            const srcField = sourceTemplate.fields.find(f => f.name === addrField);
            if (!srcField) return;
            const dstFIdx = tmpl.fields.findIndex(f => f.name === addrField);
            if (dstFIdx < 0) return;
            quoteTemplates[secId].templates[tmplIdx].fields[dstFIdx].prefill_value = srcField.prefill_value;
          });
        });
      }
    }

    setQuoteCAFTemplates(quoteTemplates);
    setIsDirty(true);
  };

  const getTemplateSiteName = (template) => {
    if (!template.has_site) return null;
    const siteId = template.site_id ?? null;
    return data.Quote.QuoteSites.find(s => s.QuoteSiteId === siteId)?.Name || 'Default';
  };

  const renderSectionFields = (category, section_index, template) => {
    return template.fields.filter(f => !f.hidden).map((field) => {
      const fieldMeta = {
        category,
        sectionIndex: section_index,
        fieldName: field.name,
        fieldIndex: template.fields.indexOf(field),
      };

      return (
        <FieldRow key={field.name}>
          <FieldLabel>
            {field.title || <span className="text-muted">No label</span>} {field.validation?.isRequired && <span className="text-danger">*</span>}
          </FieldLabel>
          <FieldInput>
            {renderFields(field, { fieldMeta, onFieldChange })}
          </FieldInput>
        </FieldRow>
      );
    });
  };

  return (
    <React.Fragment>
      {leavePrompt}
      {disableQuote && <div className="overlay"></div>}
      {invalidInput && invalidInput.error && (
        <SweetAlert
          warning
          confirmBtnText={'Update field'}
          title={
            <span style={{ fontSize: '24px' }}>Validation Form Error</span>
          }
          onConfirm={() => {
            setInvalidInput(false);
            const field = document.querySelector('.is-invalid');
            if (field) {
              field.scrollIntoView({ block: 'center' });
              field.focus();
            }
          }}
          closeOnClickOutside={false}
        >
          {invalidInput.message}
        </SweetAlert>
      )}

      {confirmation && (
        <SweetAlert
          confirmBtnText={'Wait, I need to make some changes first'}
          showCancel={true}
          onConfirm={() => setConfirmation(false)}
          onCancel={saveQuoteCAFTemplate}
          cancelBtnText={'I AM IRON MAN!'}
          cancelBtnBsStyle="secondary"
          confirmBtnStyle={{ fontSize: '8pt' }}
          cancelBtnStyle={{
            fontSize: '8pt',
            backgroundColor: '#AA0000',
            borderColor: '#AA0000',
          }}
          closeOnClickOutside={false}
          style={{
            width: '45%',
            margin: 'auto',
          }}
        >
          <div className="confirmation-modal">
            <div className="confirmation-modal-image">
              <img src={ironMan} alt="" />
            </div>
            <div className="confirmation-modal-text">
              Proceeding to the next step will prepare the proposal for the customer to sign.
              Any links they may have been sent to the proposal previously will allow them to sign and accept the agreement.
            </div>
          </div>
        </SweetAlert>
      )}

      {
        // load live search and quote item builder UI when quote with the ID passed on the URL exists
        data && data.Quote && data.CAFTemplates && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">{data.Quote.Description} - Prefill CAF</h4>
                  </Col>
                </Row>
              </div>
              <h6>PRE-FILL CAF</h6>

              <Card>
                <CardBody>
                  <Row>
                    <Col lg={{ size: 8, offset: 2 }}>
                      {(data.CAFSections || []).map((section) => {
                        const sectionId = String(section.CAFSectionId);
                        const sectionData = quoteCAFTemplates[sectionId];
                        if (!sectionData?.templates?.length) return null;
                        const isOpen = toggle[sectionId]?.isOpen !== false;
                        return (
                        <Section key={sectionId}>
                          <SectionHeader
                            onClick={() =>
                              setToggle({
                                ...toggle,
                                [sectionId]: { isOpen: !isOpen },
                              })
                            }
                          >
                            {section.Name}
                            {isOpen ? (
                              <i className="fas fa-caret-up"></i>
                            ) : (
                              <i className="fas fa-caret-down"></i>
                            )}
                          </SectionHeader>

                          {isOpen &&
                            sectionData.templates.filter(t => !t.hidden).map(
                              (template) => (
                                <Template
                                  key={template.section_name}
                                >
                                  <h6 className="mt-0" style={{ fontSize: 16, marginBottom: 15 }}>
                                    {template.section_name}
                                    {template.site_id && (': ' + getTemplateSiteName(template))}
                                  </h6>
                                  {template.propagate_address && getPropagationPeers(template).length > 0 && (
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`prop-${sectionId}-${sectionData.templates.indexOf(template)}`}
                                        checked={!template.disable_propagation}
                                        onChange={() => onTogglePropagation(sectionId, sectionData.templates.indexOf(template))}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`prop-${sectionId}-${sectionData.templates.indexOf(template)}`}
                                      >
                                        Sync site address across sections
                                      </label>
                                    </div>
                                  )}
                                  {renderSectionFields(
                                    sectionId,
                                    sectionData.templates.indexOf(template),
                                    template
                                  )}
                                </Template>
                              )
                            )}
                        </Section>
                        );
                      })}
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <button
                        className="btn-icon btn btn-light btn-lg float-left"
                        onClick={goToPreviousStep}
                      >
                        {' '}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-back mr-2"></i>
                        </span>{' '}
                        Previous Step
                      </button>
                      <button
                        className="btn-icon btn btn-primary btn-lg float-right"
                        onClick={proceedNextStep}
                      >
                        {' '}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-forward mr-2"></i>
                        </span>{' '}
                        Next Step
                      </button>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
      {
        // when data is loaded and quote id is not valid show quote does not exists error UI
        data && data.Quote === null && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">Error loading this page</h4>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      Quote with id {props.quoteId} does not exist.
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(QuoteBuilderPrefillCAF);
